import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Upload } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import { useAddMeditationTrack } from "../../application/useMeditationTracks";
import { MeditationTrack } from "../../domain/types";
import { probeAudioDuration } from "../../domain/audioProbe";

type Props = {
    /** Handed the track once it is stored, so a caller can select it. */
    onAdded?: (track: MeditationTrack) => void;
    /** Onto the centre's shelf rather than the therapist's own. */
    forCenter?: boolean;
};

/** What a browser will let a therapist pick as background music. */
const ACCEPTED_AUDIO = "audio/mpeg,audio/mp4,audio/wav,audio/ogg,.mp3,.m4a";

/**
 * Putting a piece of music on the shelf.
 *
 * The same control wherever music can be added — the library page and the
 * picker in the studio — because uploading is the same act in both, and a
 * therapist who learned it in one place should not find a different form in
 * the other.
 *
 * It asks them to confirm the rights before it will send the file, and the
 * API refuses a track that arrives without that. We cannot check where a piece
 * of music came from, and a published meditation carries its bed to whoever
 * adds it: asking the only person who knows is the honest thing available.
 */
export default function MeditationTrackUploader({ onAdded, forCenter = false }: Props) {
    const { t } = useTranslation();
    const addTrack = useAddMeditationTrack();
    const fileRef = useRef<HTMLInputElement>(null);

    const [pending, setPending] = useState<File | null>(null);
    const [rights, setRights] = useState(false);
    const [failed, setFailed] = useState(false);

    const reset = () => {
        setPending(null);
        setRights(false);
        if (fileRef.current) fileRef.current.value = "";
    };

    const add = async () => {
        if (!pending || !rights) return;
        setFailed(false);
        try {
            // Measured here, from the file itself: nothing else will know the
            // length later without downloading the whole track to find out.
            const durationSeconds = await probeAudioDuration(pending);
            const created = await addTrack.mutateAsync({
                file: pending,
                name: pending.name.replace(/\.[^.]+$/, ""),
                durationSeconds: durationSeconds || null,
                forCenter,
            });
            onAdded?.(created);
            reset();
        } catch {
            setFailed(true);
        }
    };

    return (
        <div className="min-w-0">
            {pending ? (
                <div className="rounded-xl border border-border-subtle p-3">
                    <p className="mb-2 truncate text-sm text-content-primary">
                        {pending.name}
                    </p>
                    <label className="flex items-start gap-2 text-xs text-content-secondary">
                        <input
                            type="checkbox"
                            checked={rights}
                            onChange={(e) => setRights(e.target.checked)}
                            className="mt-0.5 size-4 accent-[var(--color-accent)]"
                        />
                        <span>{t("meditation.tracks.rightsConfirm")}</span>
                    </label>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            disabled={!rights}
                            loading={addTrack.isPending}
                            onClick={() => void add()}
                        >
                            {t(forCenter ? "meditation.tracks.addToCenter" : "meditation.tracks.add")}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={reset}>
                            {t("common.cancel")}
                        </Button>
                    </div>
                </div>
            ) : (
                <Button
                    size="sm"
                    variant="outline"
                    startIcon={<Upload size={15} />}
                    onClick={() => fileRef.current?.click()}
                >
                    {t(forCenter ? "meditation.tracks.uploadToCenter" : "meditation.tracks.upload")}
                </Button>
            )}

            <input
                ref={fileRef}
                type="file"
                accept={ACCEPTED_AUDIO}
                className="sr-only"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                        setPending(file);
                        setRights(false);
                    }
                }}
            />

            <p className="mt-2 text-xs text-content-tertiary">
                {t("meditation.tracks.rightsNotice")}
            </p>

            {addTrack.isPending && (
                <p className="mt-2 flex items-center gap-2 text-xs text-content-secondary">
                    <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                    {t("meditation.tracks.uploading")}
                </p>
            )}
            {failed && (
                <p role="alert" className="mt-2 text-xs text-status-critical">
                    {t("meditation.tracks.failed")}
                </p>
            )}
        </div>
    );
}
